// Registry mapping an article's `artifact` key to its interactive component
// plus the metadata used by the Artifact Library page. readArticle.jsx renders
// nothing when a key is missing, so an article can reference an artifact that
// does not exist yet without breaking the page.

import AIReadinessScorecard from "./AIReadinessScorecard";
import DeployRiskAssessment from "./DeployRiskAssessment";
import TEVVTemplate from "./TEVVTemplate";
import TokenCostCalculator from "./TokenCostCalculator";
import AgentSecurityChecklist from "./AgentSecurityChecklist";
import DecisionStallTest from "./DecisionStallTest";
import PrincipleMeasurabilityTest from "./PrincipleMeasurabilityTest";

const ARTIFACTS = {
	"ai-readiness-scorecard": {
		component: AIReadinessScorecard,
		title: "AI Readiness Scorecard",
		blurb: "Score one AI system across ownership, validation, monitoring, and governance.",
		articleSlug: "innovation-credibility-sponsor-roi",
	},
	"deploy-risk-assessment": {
		component: DeployRiskAssessment,
		title: "3 Questions Before You Deploy",
		blurb: "A 60-second accountability, explainability, and reversibility check.",
		articleSlug: "three-questions-before-you-deploy-ai",
	},
	"tevv-template": {
		component: TEVVTemplate,
		title: "Minimum Viable Review Role",
		blurb: "A copy-paste template for the independent AI reviewer NIST assumes you have.",
		articleSlug: "nist-tevv-gap",
	},
	"token-cost-calculator": {
		component: TokenCostCalculator,
		title: "AI Token Cost Calculator",
		blurb: "Size reflexive AI over-use across your team and a full year.",
		articleSlug: "token-optimizing-claude-code",
	},
	"agent-security-checklist": {
		component: AgentSecurityChecklist,
		title: "Agent Security Checklist",
		blurb: "Eight controls to clear before an agentic system touches production.",
		articleSlug: "owasp-agent-security-gap",
	},
	"decision-stall-test": {
		component: DecisionStallTest,
		title: "The Accountability Split",
		blurb: "Accountability is not shared equally. Pick the people in a decision and see how it splits by role authority. A VP always outweighs a developer, and fewer names mean more weight each.",
		articleSlug: "the-accountability-split",
	},
	"principle-measurability-test": {
		component: PrincipleMeasurabilityTest,
		title: "Can You Measure That AI Principle?",
		blurb: "Run one of your AI principles through the five steps PROBE uses to turn it into a score. Find out whether you have a measurement or a slogan.",
		articleSlug: "measuring-ai-principles",
	},
	"ai-is-not-magic": {
		// External interactive site, not an in-article artifact. Rendered on the
		// /tools page as an outbound card via externalUrl (no `component`).
		title: "AI Is Not Magic",
		blurb: "A plain-language, interactive walk through how AI actually works and where it breaks. Nine short scenes, each a live demo. Built for anyone who finds AI intimidating.",
		externalUrl: "https://notmagic.swapniltamse.com",
		articleSlug: "ai-is-not-magic",
	},
	"meat-proxy-self-check": {
		// External interactive site. Outbound card on /tools via externalUrl.
		title: "Meat Proxy or Author?",
		blurb: "A two-minute behavioral self-check that plots you on two axes, whether you understand AI output and whether you will stand behind it, into four types: Pipe, Narrator, Bluffer, Author. Built on Niklas Gruhn's essay.",
		externalUrl: "https://meatproxy.swapniltamse.com",
		articleSlug: "meat-proxy-self-check",
	},
	"jevlis-ka-decoder": {
		// External interactive site. Outbound card on /tools via externalUrl.
		title: "Jevlis ka? Reply Decoder",
		blurb: "Paste a polite reply like \"No worries!\" and see how sure an AI model is that the person meant it. Built on Jev, which answers with probabilities instead of text, with my test results and where it breaks.",
		externalUrl: "https://jevliska.swapniltamse.com",
		articleSlug: "jevlis-ka-building-with-jev",
	},
};

export function getArtifact(key) {
	if (!key) return null;
	return ARTIFACTS[key] ? ARTIFACTS[key].component : null;
}

// Full metadata for a tool, including its component and source article, used by
// the standalone /tool/:key page. Returns null for unknown keys and for
// external-only entries (no in-app component to render on their own page).
export function getArtifactMeta(key) {
	if (!key) return null;
	const a = ARTIFACTS[key];
	if (!a || !a.component) return null;
	return {
		key,
		component: a.component,
		title: a.title,
		blurb: a.blurb,
		articleSlug: a.articleSlug,
		topic: TOOL_TOPIC[key] || "More",
	};
}

// Curated topic per tool, powering the filter chips on /tools. Kept to the
// same taxonomy as the articles filter; security folds into governance.
const TOOL_TOPIC = {
	"ai-readiness-scorecard": "AI Governance",
	"deploy-risk-assessment": "AI Governance",
	"tevv-template": "AI Governance",
	"token-cost-calculator": "Building",
	"agent-security-checklist": "AI Governance",
	"decision-stall-test": "Leadership",
	"principle-measurability-test": "AI Governance",
	"ai-is-not-magic": "Building",
	"meat-proxy-self-check": "Building",
	"jevlis-ka-decoder": "AI Governance",
};

export const TOOL_TOPICS = ["AI Governance", "Leadership", "Building"];

export function listArtifacts() {
	return Object.entries(ARTIFACTS).map(([key, value]) => ({
		key,
		title: value.title,
		blurb: value.blurb,
		articleSlug: value.articleSlug,
		externalUrl: value.externalUrl || null,
		topic: TOOL_TOPIC[key] || "More",
	}));
}

export default ARTIFACTS;
